import React, { useEffect } from "react";
import SwaggerUI from 'swagger-ui-react';
import { getSwagggerDocUrl } from "../config";
import { env } from "../helpers/GlobalStateProvider";
import 'swagger-ui-react/swagger-ui.css';
import './custom-swagger.css';
import { Layout } from "../components/Layout";
import { Box, Code, Flex, Heading, Link, List, Text } from "@chakra-ui/react";

{/* @ts-expect-error Server Component */}
const SwaggerComponent = () => <SwaggerUI url={getSwagggerDocUrl(env)} />

const swaggerUrl = getSwagggerDocUrl(env);

// ── Shared style primitives ───────────────────────────────────────────────────

const SectionHeading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Heading
    as="h2"
    fontSize="30px"
    fontWeight="bold"
    letterSpacing="-0.4px"
    lineHeight="32px"
    color="#18181b"
  >
    {children}
  </Heading>
);

const SubHeading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Heading
    as="h3"
    fontSize="20px"
    fontWeight="bold"
    lineHeight="28px"
    color="#18181b"
  >
    {children}
  </Heading>
);

// Rendered as a div (not the default <p>) because some sections nest block
// elements (code blocks) inside their body copy.
const BodyText: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Text as="div" fontSize="md" color="#52525b" lineHeight="24px">
    {children}
  </Text>
);

const ExternalLink: React.FC<React.PropsWithChildren<{ href: string }>> = ({
  href,
  children,
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    color="#9333ea"
    fontWeight="semibold"
    overflowWrap="anywhere"
  >
    {children}
  </Link>
);

const CodeBlock: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    as="pre"
    bg="#f4f4f5"
    color="#18181b"
    border="1px solid"
    borderColor="#e4e4e7"
    borderRadius="8px"
    px="12px"
    py="12px"
    fontFamily="mono"
    fontSize="13px"
    lineHeight="20px"
    overflowX="auto"
    whiteSpace="pre-wrap"
    wordBreak="break-word"
  >
    {children}
  </Box>
);

const InlineCode: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Code
    bg="#f4f4f5"
    color="#18181b"
    border="1px solid"
    borderColor="#e4e4e7"
    borderRadius="4px"
    px="4px"
    fontSize="0.875em"
  >
    {children}
  </Code>
);

// ── Tier data ─────────────────────────────────────────────────────────────────

const tiers = [
  {
    name: "Free Tier",
    features: [
      "500 credits per month",
      "5 API calls per minute",
      "NO web-socket access",
    ],
  },
  {
    name: "Builder Tier",
    features: [
      "50,000 credits per month",
      "30 API calls per minute",
      "NO web-socket access",
    ],
  },
  {
    name: "Growth Tier",
    features: [
      "450,000 credits per month",
      "90 API calls per minute",
      "Full web-socket access",
    ],
  },
  {
    name: "Professional Tier",
    features: [
      "1,500,000 credits per month",
      "300 API calls per minute",
      "Full web-socket access",
    ],
  },
  {
    name: "Enterprise Tier",
    features: [
      "5,000,000 credits per month",
      "900 API calls per minute",
      "Full web-socket access",
    ],
  },
];

const TierBlock: React.FC<{ name: string; features: string[] }> = ({
  name,
  features,
}) => (
  <Box>
    <Text fontSize="md" fontWeight="bold" lineHeight="24px" color="#18181b">
      {name}
    </Text>
    <List.Root
      listStyleType="disc"
      listStylePosition="outside"
      ps="20px"
      mt="8px"
      gap="2px"
    >
      {features.map((feature) => (
        <List.Item
          key={feature}
          fontSize="md"
          color="#52525b"
          lineHeight="24px"
        >
          {feature}
        </List.Item>
      ))}
    </List.Root>
  </Box>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ApiDocs: React.FC = () => {
  useEffect(() => {
    document.title = "CoinCap API Documentation";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "CoinCap API — real-time cryptocurrency prices, market caps, and exchange data. Supports accountless x402 payment protocol for agent access: create a prepaid API key with POST /v3/prepaid/create (100 free credits, refill with USDC on Base). OpenAPI spec: " + swaggerUrl);
  }, []);

  return (
    <Layout behavior="docs">
      {/* Main content container */}
      <Flex flex={1} justifyContent="center" alignItems="flex-start" p="20px">
        {/* Wrap the content in a column with some reasonable max width */}
        <Flex direction="column" gap="28px" maxWidth="680px" width="100%">

          <SectionHeading>CoinCap API 3.0</SectionHeading>

          <BodyText>
            CoinCap is a useful tool for real-time pricing and market activity
            for over 1,000 cryptocurrencies. By collecting exchange data from
            thousands of markets, we are able to offer transparent and
            accurate data on asset price and availability.
            <br />
            <br />
            Our API will offer insight into exactly which exchanges and
            markets contribute to our pricing.
            <br />
            <br />
            For all endpoints, a single page offers 100 responses by default
            and supports up to 2,000 responses per page upon requests.
            <br />
            <br />
            You can purchase API access, generate keys, and monitor API usage
            via our dashboard at:{" "}
            <ExternalLink href="https://pro.coincap.io/dashboard">
              pro.coincap.io/dashboard
            </ExternalLink>
          </BodyText>

          <SubHeading>Access and Limits</SubHeading>

          <Flex direction="column" gap="24px">
            <BodyText>
              We track API usage by API credits. Each API call charges a base
              cost of 1 credit, and then 1 additional credit per 2,500 bytes of
              additional data. The web-socket charges 1 credit per minute of
              use. Rate limits vary by tier, from 5 API calls per minute on the
              Free tier up to 900 on Enterprise.
              <br />
              <br />

              Responses to identical requests may be served from an in-memory
              cache for up to 60 seconds to improve performance. Cached
              responses are billed the same as fresh responses.
              <br />
              <br />

              All API calls require a bearer token in the header or URL.
              <br />
              <br />
              <ExternalLink href="https://pro.coincap.io/dashboard">
                Signup for a key
              </ExternalLink>
            </BodyText>

            {tiers.map((tier) => (
              <TierBlock key={tier.name} name={tier.name} features={tier.features} />
            ))}
          </Flex>

          <SectionHeading>REST API Documentation</SectionHeading>

          <BodyText>
            Our REST API can be accessed via the following URL:
            <strong> rest.coincap.io/v3/... </strong>
            <br />
            We have included our OpenAPI Spec & Swagger documentation below where you
            can test and experiment with API V3. Please note that using these
            Swagger components also requires a bearer token included in the
            header. To automatically add your API key to each call made through
            Swagger, click the ‘Authorize’ button below and paste in your API
            key.
          </BodyText>

          <SwaggerComponent />

          {/* MCP API Section */}
          <Box mt="36px">
            <SectionHeading>MCP API (Model Context Protocol)</SectionHeading>
          </Box>

          <BodyText>
            CoinCap's <b>MCP server</b> exposes all core REST API functionality via the <ExternalLink href="https://modelcontextprotocol.io/">Model Context Protocol</ExternalLink> for use with AI assistants, LLMs, and other MCP-compatible tooling.
            <br /><br />
            The MCP server proxies and mirrors all endpoints documented above in the REST API (see Swagger), but presents them in the MCP JSON-RPC format. This makes it easy to integrate real-time crypto data into AI workflows and agents.
            <br /><br />
            <b>Remote MCP Server Base URL:</b><br />
            <InlineCode>https://rest.coincap.io/mcp</InlineCode>
            <br /><br />
            <b>GitHub:</b> <ExternalLink href="https://github.com/CC3-0/coincap-mcp-server">CC3-0/coincap-mcp-server</ExternalLink>
            <br />
            <b>NPM:</b> <ExternalLink href="https://www.npmjs.com/package/@coincap/coincap-mcp-server">@coincap/coincap-mcp-server</ExternalLink>
            <br />
            <b>DXT File:</b> <ExternalLink href="https://github.com/CC3-0/coincap-mcp-server/raw/refs/heads/main/coincap-mcp-clean.dxt">Download Claude Desktop Extension</ExternalLink>
          </BodyText>

          <SubHeading>MCP Quickstart</SubHeading>
          <BodyText>
            The easiest way to see the coincap MCP server in action is to download our Claude Desktop Extension (DXT) file above and open it with Claude Desktop.
            <br />
            <br />
            Just create an apiKey in your dashboard, then open the DXT file in Claude Desktop and set your apiKey in the settings.
            <br />
            <br />
            Visit  <ExternalLink href="https://www.anthropic.com/engineering/desktop-extensions">https://www.anthropic.com/engineering/desktop-extensions</ExternalLink> to learn more about Claude Desktop Extensions
          </BodyText>

          <SubHeading>Discover & Call Tools</SubHeading>
          <BodyText>
            To discover all available tools/endpoints, use <InlineCode>tools/list</InlineCode>:
          </BodyText>

          <CodeBlock>
            {`curl -X POST https://rest.coincap.io/mcp -H "Content-Type: application/json" -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'`}
          </CodeBlock>

          <BodyText>
            To call any endpoint/tool, use <InlineCode>tools/call</InlineCode> and provide the tool name and arguments:<br />
          </BodyText>

          <CodeBlock>
            {`curl -X POST https://rest.coincap.io/mcp -H "Content-Type: application/json" -d '{"jsonrpc":"2.0","id":2,"method":"tools/call","params":{"name":"assets_slug","arguments":{"slug":"bitcoin","apiKey":"YOUR_API_KEY"}}}'`}
          </CodeBlock>

          <SubHeading>MCP Inspector</SubHeading>
          <BodyText>
            You can explore CoinCap's MCP endpoint using the open source <ExternalLink href="https://github.com/modelcontextprotocol/inspector">MCP Inspector</ExternalLink> tool.<br/>
            The Inspector can connect to any MCP endpoint over streamable HTTP/HTTPS.<br/><br/>
            <b>Start MCP Inspector:</b>
            <Box my="8px">
              <CodeBlock>npx @modelcontextprotocol/inspector</CodeBlock>
            </Box>

            Set <b>streamable https</b> with url <b>https://rest.coincap.io/mcp</b>
          </BodyText>

          <SubHeading>Claude Desktop</SubHeading>
          <BodyText>
            For instructions on how to download this server locally and run with Claude Desktop, visit our
            GitHub: <ExternalLink href="https://github.com/CC3-0/coincap-mcp-server">CC3-0/coincap-mcp-server</ExternalLink>
          </BodyText>

          <SectionHeading>Web-socket Documentation</SectionHeading>

          <BodyText>
            The web-socket is available through the Growth, Professional, and
            Enterprise tiers. Using the web-socket consumes 1 credit per minute
            the web-socket is open.
            <br />
            <br />
            The web-socket can be accessed via the following URL:{" "}
            <strong>wss://wss.coincap.io/</strong>
          </BodyText>

          <SubHeading>Prices Web-socket</SubHeading>

          <Flex direction="column" gap="16px">
            <BodyText>
              The CoinCap prices endpoint is the most accurate source of
              real-time changes to the global price of an asset. Each time the
              system receives data that moves the global price in one direction
              or another, this change is immediately published through the
              websocket. These prices correspond with the values shown in /assets
              – a value that may change several times per second based on market
              activity.
            </BodyText>

            <BodyText>Example usage with websocat:</BodyText>

            <CodeBlock>
              {`websocat "wss://wss.coincap.io/prices?assets=bitcoin,usdc&apiKey=XXX"`}
            </CodeBlock>

            <CodeBlock>
              {`websocat "wss://wss.coincap.io/prices?assets=ALL&apiKey=YourApiKey"`}
            </CodeBlock>
          </Flex>
        </Flex>
      </Flex>
    </Layout>
  );
};

export default ApiDocs;
